import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AppHeader } from '../shared/AppHeader';
import { useCheckout } from '../../context/CheckoutContext';
import { CarType, CheckoutPlan, VehicleSize } from '../../types/CheckoutType';
import { EMAIL_FIELD, REQUIRED_FIELD } from '../../utils/constants';
import { cpfValidator } from '../../utils/cpf-validator';
import { checkEmailExists, fetchAddressByCep } from '../../api/checkout';
import CpfMask from '../InputMask/CpfMask';
import PhoneMask from '../InputMask/PhoneMask';
import CepMask from '../InputMask/CepMask';
import CardNumberMask from '../InputMask/CardNumberMask';
import CardExpirationMask from '../InputMask/CardExpirationMask';
import CvvMask from '../InputMask/CvvMask';
import { pushGTMEvent } from '../../utils/gtm';

// ─── tokens (do handoff "WashPass Assinatura") ───────────────────────────────

const NAVY = '#2F3857';
const CYAN = '#0098CF';
const SELECTED_BG = '#E6F4FB';
const BORDER = '#E4E7EB';
const TEXT = '#1F2933';
const TEXT_MUTED = '#5F6670';
const TEXT_HINT = '#7A828F';
const TEXT_FAINT = '#9AA1AC';
const FEATURE_TEXT = '#454D58';
const STEP2_BG = '#F7F8FA';
const PLAN_BOX_BG = '#F2F4F8';
const DISABLED_BG = '#EDEEF1';
const DISABLED_BTN = '#C9CED6';
const DISABLED_INK = '#5F6670';
const ERROR = '#E0433A';

const formatPrice = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthlyPrice = (p: CheckoutPlan) => (p.interval_type === 'YEAR' ? p.price / 12 : p.price);
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
const CHECKOUT_STEP_NAMES = ['Seu carro', 'Pagamento'] as const;

// ─── ícones (paths do handoff) ────────────────────────────────────────────────

const Svg = ({ path, size = 24 }: { path: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d={path} />
  </svg>
);
const IconHatch = (p: { size?: number }) => (
  <Svg
    size={p.size}
    path="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"
  />
);
const IconSuv = (p: { size?: number }) => (
  <Svg
    size={p.size}
    path="M17 5H3c-1.1 0-2 .89-2 2v9h2c0 1.65 1.34 3 3 3s3-1.35 3-3h5.5c0 1.65 1.34 3 3 3s3-1.35 3-3H23v-5l-6-6zM3 11V7h4v4H3zm3 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM9 11V7h4v4H9zm8.5 6.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM15 11V7h1l4 4h-5z"
  />
);
const IconPerson = (p: { size?: number }) => (
  <Svg
    size={p.size}
    path="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
  />
);
const IconPro = (p: { size?: number }) => (
  <Svg
    size={p.size}
    path="M18.92 6.01C18.72 5.42 18.16 5 17.5 5H15V3H9v2H6.5c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"
  />
);
const IconArrowForward = () => <Svg size={17} path="M4 11h12.17l-5.58-5.59L12 4l8 8-8 8-1.41-1.41L16.17 13H4v-2z" />;
const IconCheckSm = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const IconCheckRound = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: 'none', marginTop: 1 }}>
    <circle cx="12" cy="12" r="10" fill={CYAN} />
    <path d="M17.5 8.5L10.5 15.5L7 12" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TEXT_HINT} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none', marginTop: 1 }}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ─── schema (etapa 2) ────────────────────────────────────────────────────────

const schema = yup.object({
  full_name: yup.string().required(REQUIRED_FIELD).min(2, 'Nome muito curto'),
  email: yup.string().required(REQUIRED_FIELD).email(EMAIL_FIELD),
  phone: yup.string().required(REQUIRED_FIELD).matches(/^\d{10,11}$/, 'Celular inválido'),
  document: yup
    .string()
    .required(REQUIRED_FIELD)
    .test('cpf', 'CPF inválido', (v) => cpfValidator(v || '')),
  password: yup.string().required(REQUIRED_FIELD).min(6, 'Mínimo de 6 caracteres'),
  zip_code: yup.string().required(REQUIRED_FIELD).matches(/^\d{8}$/, 'CEP inválido'),
  street: yup.string().required(REQUIRED_FIELD),
  number: yup.string().required(REQUIRED_FIELD),
  neighborhood: yup.string().required(REQUIRED_FIELD),
  city: yup.string().required(REQUIRED_FIELD),
  state: yup.string().required(REQUIRED_FIELD).max(2, 'Use a sigla'),
  complement: yup.string().optional(),
  cardNumber: yup
    .string()
    .required(REQUIRED_FIELD)
    .test('len', 'Número do cartão inválido', (v) => (v || '').replace(/\D/g, '').length >= 13),
  cardName: yup.string().required(REQUIRED_FIELD),
  expiration: yup.string().required(REQUIRED_FIELD).matches(/^\d{2}\/\d{2}$/, 'MM/AA'),
  cvv: yup.string().required(REQUIRED_FIELD).min(3, 'CVV inválido').max(4, 'CVV inválido'),
  couponCode: yup.string().optional(),
  privacy_terms: yup.boolean().oneOf([true], 'Aceite a política de privacidade'),
  program_rules: yup.boolean().oneOf([true], 'Aceite os termos do programa'),
  receive_communications: yup.boolean(),
});

type FormData = yup.InferType<typeof schema>;

// ─── UI: campo de formulário ─────────────────────────────────────────────────

const fieldLabelSx = { mb: 0.75, fontWeight: 700, color: NAVY, fontSize: '0.8rem' };
const fieldInputSx = (hasError?: boolean) => ({
  borderRadius: '12px',
  backgroundColor: 'white',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? ERROR : BORDER, borderWidth: 1.5 },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? ERROR : '#C9CED6' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: hasError ? ERROR : CYAN },
});

const Field = ({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <Box>
    <InputLabel sx={fieldLabelSx}>{label}</InputLabel>
    {children}
    {error && <FormHelperText sx={{ color: ERROR, fontWeight: 600 }}>{error}</FormHelperText>}
  </Box>
);

const FormBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      backgroundColor: 'white',
      borderRadius: '18px',
      border: `1px solid ${BORDER}`,
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
      mb: 2.25,
    }}
  >
    {children}
  </Box>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontWeight: 800, fontSize: '1.19rem', color: NAVY, letterSpacing: '-0.3px', mb: 1.5 }}>
    {children}
  </Typography>
);

// ─── UI: card de opção (tamanho / uso) ───────────────────────────────────────

const OptionCard = ({
  icon,
  label,
  hint,
  selected,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  selected: boolean;
  onClick: () => void;
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    sx={{
      position: 'relative',
      textAlign: 'left',
      cursor: 'pointer',
      p: '16px 14px',
      borderRadius: '16px',
      backgroundColor: selected ? SELECTED_BG : 'white',
      border: '1.5px solid',
      borderColor: selected ? CYAN : BORDER,
      boxShadow: selected ? '0 8px 22px -10px rgba(0,152,207,.6)' : '0 1px 2px rgba(31,41,51,.04)',
      transition: 'all .2s ease',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', height: 34, mb: 1, color: selected ? NAVY : TEXT_FAINT }}>
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: TEXT, mb: 0.4 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.35, color: TEXT_HINT }}>{hint}</Typography>
    <Box
      sx={{
        position: 'absolute',
        top: 11,
        right: 11,
        width: 22,
        height: 22,
        borderRadius: '50%',
        backgroundColor: CYAN,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: selected ? 1 : 0,
        transform: selected ? 'scale(1)' : 'scale(.4)',
        transition: 'all .2s ease',
      }}
    >
      <IconCheckSm />
    </Box>
  </Box>
);

// ─── UI: card de plano ───────────────────────────────────────────────────────

const PlanCard = ({
  plan,
  selected,
  onClick,
}: {
  plan: CheckoutPlan;
  selected: boolean;
  onClick: () => void;
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
    sx={{
      position: 'relative',
      textAlign: 'left',
      cursor: 'pointer',
      p: '16px 16px 15px',
      borderRadius: '18px',
      backgroundColor: selected ? SELECTED_BG : 'white',
      border: '1.5px solid',
      borderColor: selected ? CYAN : BORDER,
      boxShadow: selected ? '0 8px 22px -10px rgba(0,152,207,.6)' : '0 1px 2px rgba(31,41,51,.04)',
      transition: 'all .2s ease',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 4 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: TEXT, letterSpacing: '-0.3px' }}>
        {plan.name}
      </Typography>
      {plan.tag && (
        <Box sx={{ px: 1, py: 0.4, borderRadius: '6px', backgroundColor: SELECTED_BG }}>
          <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: NAVY, letterSpacing: '0.02em' }}>
            {plan.tag}
          </Typography>
        </Box>
      )}
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, mt: 0.75, mb: plan.interval_type === 'YEAR' ? 0.25 : 1.1 }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.56rem', color: TEXT, letterSpacing: '-1px' }}>
        R$ {formatPrice(monthlyPrice(plan))}
      </Typography>
      <Typography sx={{ color: TEXT_HINT, fontSize: '0.8rem' }}>/mês</Typography>
    </Box>
    {plan.interval_type === 'YEAR' && (
      <Typography sx={{ color: TEXT_FAINT, fontSize: '0.78rem', mb: 1.1 }}>
        cobrado anualmente — R$ {formatPrice(plan.price)}/ano
      </Typography>
    )}

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      {plan.carwash_count != null && plan.carwash_count > 0 && (
        <BenefitRow text={`${plan.carwash_count} locais na rede`} />
      )}
      {(plan.plan_services ?? []).map((s) => (
        <BenefitRow key={s.service.id} text={`${s.service.name}${s.limit > 0 ? ` (até ${s.limit}x/mês)` : ''}`} />
      ))}
      {!plan.plan_services?.length && plan.description && <BenefitRow text={plan.description} />}
    </Box>

    <Box
      sx={{
        position: 'absolute',
        top: 14,
        right: 14,
        width: 24,
        height: 24,
        borderRadius: '50%',
        backgroundColor: CYAN,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: selected ? 1 : 0,
        transform: selected ? 'scale(1)' : 'scale(.4)',
        transition: 'all .2s ease',
      }}
    >
      <IconCheckSm size={13} />
    </Box>
  </Box>
);

const BenefitRow = ({ text }: { text: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <IconCheckRound />
    <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35, color: FEATURE_TEXT }}>{text}</Typography>
  </Box>
);

// ─── UI: resumo "Seu plano" (etapa 1) ────────────────────────────────────────

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <>
    <Typography sx={{ fontSize: '0.844rem', color: TEXT_HINT }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.844rem', fontWeight: 700, color: TEXT, textAlign: 'right' }}>{value}</Typography>
  </>
);

const PlanSummaryBox = ({
  planName,
  sizeLabel,
  useLabel,
  cycleLabel,
  totalPrice,
  yearNote,
  emptyHint,
  features,
}: {
  planName: string;
  sizeLabel: string;
  useLabel: string;
  cycleLabel: string;
  totalPrice: string;
  yearNote?: string;
  emptyHint?: string;
  features: string[];
}) => (
  <Box sx={{ mt: 2.75, p: '18px', borderRadius: '18px', backgroundColor: STEP2_BG, border: `1px solid ${BORDER}` }}>
    <Typography sx={{ fontWeight: 800, fontSize: '1.06rem', color: NAVY, letterSpacing: '-0.3px', mb: 1.75 }}>
      Seu plano
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 2,
        rowGap: 1.25,
        alignItems: 'baseline',
        pb: 1.75,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <SummaryRow label="Plano" value={planName} />
      <SummaryRow label="Carro" value={sizeLabel} />
      <SummaryRow label="Uso" value={useLabel} />
      <SummaryRow label="Cobrança" value={cycleLabel} />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, my: 1.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.844rem', color: TEXT }}>Valor</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.625rem', color: TEXT, letterSpacing: '-1px' }}>
          {totalPrice}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: TEXT_HINT }}>/mês</Typography>
      </Box>
    </Box>
    {yearNote && (
      <Typography sx={{ fontSize: '0.78rem', color: TEXT_FAINT, mb: 1.25 }}>{yearNote}</Typography>
    )}
    {emptyHint ? (
      <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.45, color: TEXT_HINT }}>{emptyHint}</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {features.map((f) => (
          <BenefitRow key={f} text={f} />
        ))}
      </Box>
    )}
  </Box>
);

// ─── UI: stepper ─────────────────────────────────────────────────────────────

const StepDot = ({ n, active }: { n: number; active: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, opacity: active ? 1 : 0.45 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: active ? CYAN : '#E8ECF3',
        color: active ? 'white' : '#5C6B87',
        fontWeight: 700,
        fontSize: '0.75rem',
      }}
    >
      {n}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: TEXT }}>
      {n === 1 ? 'Seu carro' : 'Pagamento'}
    </Typography>
  </Box>
);

const Stepper = ({ step }: { step: 1 | 2 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2.5, mb: 3, borderBottom: `1px solid #EDEEF1` }}>
    <StepDot n={1} active={step === 1} />
    <Box sx={{ flex: 1, height: 2, borderRadius: 2, backgroundColor: '#E8ECF3', overflow: 'hidden' }}>
      <Box sx={{ height: '100%', backgroundColor: CYAN, width: step === 2 ? '100%' : '0%', transition: 'width .5s ease' }} />
    </Box>
    <StepDot n={2} active={step === 2} />
  </Box>
);

// ─── UI: barra flutuante ─────────────────────────────────────────────────────

const FloatingBar = ({
  label,
  price,
  ready,
  ctaLabel,
  ctaIcon,
  onClick,
}: {
  label: string;
  price: string;
  ready: boolean;
  ctaLabel: string;
  ctaIcon: React.ReactNode;
  onClick: () => void;
}) => (
  <Box
    sx={{
      position: 'fixed',
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: { xs: 14, md: 22 },
      width: 'min(92vw, 480px)',
      zIndex: 40,
    }}
  >
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        borderRadius: '18px',
        p: '6px 6px 6px 18px',
        backgroundColor: ready ? NAVY : DISABLED_BG,
        boxShadow: ready ? '0 14px 30px -10px rgba(47,56,87,.5)' : '0 1px 2px rgba(31,41,51,.06)',
        cursor: 'pointer',
        transition: 'background .25s, box-shadow .25s',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.656rem',
            letterSpacing: '0.4px',
            color: ready ? 'rgba(255,255,255,.72)' : '#69707A',
          }}
        >
          {label.toUpperCase()}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.06rem', letterSpacing: '-0.5px', color: ready ? 'white' : '#69707A' }}>
          {price}
        </Typography>
      </Box>
      <Box
        sx={{
          ml: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '14px',
          px: 2,
          py: 1.6,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          fontWeight: 800,
          fontSize: '0.875rem',
          backgroundColor: ready ? CYAN : DISABLED_BTN,
          color: ready ? 'white' : DISABLED_INK,
        }}
      >
        {ctaIcon}
        {ctaLabel}
      </Box>
    </Box>
  </Box>
);

// ─── UI: overlay de sucesso ──────────────────────────────────────────────────

const SuccessOverlay = ({ line, onOpenApp }: { line: string; onOpenApp: () => void }) => (
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      zIndex: 200,
      backgroundColor: NAVY,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2.5,
      p: 5,
    }}
  >
    <Box
      sx={{
        width: 92,
        height: 92,
        borderRadius: '50%',
        backgroundColor: CYAN,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 14px rgba(0,152,207,.16)',
      }}
    >
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </Box>
    <Typography sx={{ fontWeight: 800, fontSize: '1.625rem', color: 'white', letterSpacing: '-0.6px', textAlign: 'center' }}>
      Assinatura ativa
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,.62)', fontSize: '0.875rem', lineHeight: 1.55, textAlign: 'center', maxWidth: 280 }}>
      {line}
    </Typography>
    <Button
      onClick={onOpenApp}
      sx={{
        mt: 0.5,
        backgroundColor: 'white',
        color: NAVY,
        fontWeight: 800,
        fontSize: '0.875rem',
        px: 3.25,
        py: 1.75,
        borderRadius: '14px',
        '&:hover': { backgroundColor: '#f1f5f9' },
      }}
    >
      Abrir o app
    </Button>
  </Box>
);

// ─── componente principal ────────────────────────────────────────────────────

const CheckoutForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plans, plansLoading, plansError, loading, error, initialData, initialPlanId, submitCheckout, clearError } =
    useCheckout();

  const planNameParam = (searchParams.get('plan') || searchParams.get('plan_name') || '').trim();
  const forcedSize = (searchParams.get('size') || '').toUpperCase() as VehicleSize | '';
  const wantPeriod: 'MONTH' | 'YEAR' = searchParams.get('period') === 'anual' ? 'YEAR' : 'MONTH';
  const wantsProfessional = searchParams.get('professional') === '1';

  const [step, setStep] = useState<1 | 2>(1);
  const [done, setDone] = useState(false);
  const [carType, setCarType] = useState<CarType | null>(forcedSize === 'G' ? 'suv' : forcedSize === 'M' ? 'hatch' : null);
  const [isProfessional, setIsProfessional] = useState<boolean | null>(
    searchParams.get('professional') === '1' ? true : null,
  );
  const [period, setPeriod] = useState<'MONTH' | 'YEAR'>(wantPeriod);
  const [planId, setPlanId] = useState<number | ''>('');
  const [installments, setInstallments] = useState(1);

  const size: VehicleSize | null = carType ? (carType === 'suv' ? 'G' : 'M') : null;

  const hasYearly = useMemo(
    () =>
      plans.some(
        (p) =>
          p.interval_type === 'YEAR' &&
          !!p.only_professional === (isProfessional ?? false) &&
          (!p.size || p.size === (size ?? 'M')),
      ),
    [plans, isProfessional, size],
  );

  const scopedPlans = useMemo(() => {
    if (!size || isProfessional === null) return [];
    return plans
      .filter((p) => !!p.only_professional === isProfessional)
      .filter((p) => !p.size || p.size === size)
      .filter((p) => p.interval_type === period)
      .sort((a, b) => monthlyPrice(a) - monthlyPrice(b));
  }, [plans, size, isProfessional, period]);

  const selectedPlan = plans.find((p) => p.id === planId) || null;
  const ready = !!(carType && isProfessional !== null && selectedPlan);

  const sizeLabel = carType ? (carType === 'suv' ? 'SUV / grande' : 'Hatch / Sedan') : 'Não selecionado';
  const useLabel = isProfessional === null ? 'Não selecionado' : isProfessional ? 'Profissional' : 'Pessoal';
  const cycleLabel = period === 'YEAR' ? 'Anual' : 'Mensal';
  const emptyHint = !carType
    ? 'Escolha o tamanho do carro para começar.'
    : isProfessional === null
      ? 'Agora diga como você usa o veículo.'
      : 'Selecione um plano para continuar.';

  // Trocar porte, uso ou ciclo muda a lista disponível — limpa o plano escolhido.
  // Fica direto nos handlers de clique (não em useEffect observando o estado)
  // pra não competir com a pré-seleção, que já define o plano certo sozinha.
  const pickCarType = (id: CarType) => {
    if (id !== carType) {
      setPlanId('');
      setInstallments(1);
    }
    setCarType(id);
  };
  const pickProfessional = (value: boolean) => {
    if (value !== isProfessional) {
      setPlanId('');
      setInstallments(1);
    }
    setIsProfessional(value);
    pushGTMEvent({ event: 'complete_perfil' });
  };
  const pickPeriod = (value: 'MONTH' | 'YEAR') => {
    if (value !== period) {
      setPlanId('');
      setInstallments(1);
    }
    setPeriod(value);
    pushGTMEvent({ event: 'checkout_period_toggled', period: value });
  };

  // pré-seleção pelo link da home (roda uma vez, quando os planos chegam)
  const preselectDone = useRef(false);
  useEffect(() => {
    if (preselectDone.current || !plans.length) return;
    preselectDone.current = true;

    let target: CheckoutPlan | undefined;
    if (initialPlanId) target = plans.find((p) => p.id === initialPlanId);
    if (!target && planNameParam) {
      // "WASHELITE" bate com o canonical_name do plano ("Wash Elite"); o name
      // real ("Elite G Mensal") varia por porte/ciclo, por isso o fallback.
      const t = norm(planNameParam.replace(/[\s\-_]+[mg]$/i, ''));
      // em homolog o "nome de marketing" mora em canonical_name ("Wash Elite");
      // em produção canonical_name vem vazio e o próprio name já é "Wash Elite"
      // (ou "Wash Pro Basic", que a home manda sem o prefixo "Wash").
      const stripWash = (s: string) => s.replace(/^wash/, '');
      const byCanonical = plans.filter((p) => norm(p.canonical_name || '') === t);
      const byName = plans.filter(
        (p) => norm(p.name) === t || stripWash(norm(p.name)) === stripWash(t),
      );
      const candidates = byCanonical.length ? byCanonical : byName;

      target =
        candidates.find(
          (p) =>
            !!p.only_professional === wantsProfessional &&
            p.interval_type === wantPeriod &&
            (!forcedSize || !p.size || p.size === forcedSize),
        ) ||
        candidates.find(
          (p) => !!p.only_professional === wantsProfessional && (!forcedSize || !p.size || p.size === forcedSize),
        ) ||
        candidates.find((p) => !!p.only_professional === wantsProfessional) ||
        candidates[0];
    }
    if (!target) return;

    if (target.size) setCarType(target.size === 'G' ? 'suv' : 'hatch');
    setIsProfessional(!!target.only_professional);
    setPeriod(target.interval_type);
    setPlanId(target.id);
  }, [plans, initialPlanId, planNameParam]);

  // ── etapa 2: formulário ──
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, touchedFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as never,
    mode: 'onBlur',
    defaultValues: {
      full_name: initialData?.full_name || '',
      email: initialData?.email || '',
      phone: '',
      document: '',
      password: '',
      zip_code: '',
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      complement: '',
      cardNumber: '',
      cardName: '',
      expiration: '',
      cvv: '',
      couponCode: '',
      privacy_terms: false,
      program_rules: false,
      receive_communications: false,
    },
  });

  // RHF só calcula isValid de verdade depois da 1ª validação — força uma
  // logo ao entrar na etapa 2, pra o botão já nascer desabilitado. Isso não
  // deve deixar os campos vermelhos antes do usuário mexer neles, então a
  // exibição do erro é filtrada por campo tocado / tentativa de envio (ver
  // shownError abaixo).
  useEffect(() => {
    trigger();
  }, [trigger]);

  // Só mostra o erro de um campo depois que o usuário mexeu nele (blur) ou
  // tentou enviar o formulário — nunca antes disso.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const shownError = (name: keyof FormData) =>
    touchedFields[name] || submitAttempted ? errors[name]?.message : undefined;

  const [cardNumber, cardName, expiration, cvv] = watch(['cardNumber', 'cardName', 'expiration', 'cvv']);
  const paymentInfoFired = useRef(false);
  useEffect(() => {
    if (paymentInfoFired.current) return;
    if (cardNumber && cardName && expiration && cvv) {
      pushGTMEvent({ event: 'payment_info' });
      paymentInfoFired.current = true;
    }
  }, [cardNumber, cardName, expiration, cvv]);

  const zip = watch('zip_code');
  useEffect(() => {
    if (!zip || zip.replace(/\D/g, '').length !== 8) return;
    fetchAddressByCep(zip).then((d) => {
      if (!d) return;
      if (d.street) setValue('street', d.street);
      if (d.neighborhood) setValue('neighborhood', d.neighborhood);
      if (d.city) setValue('city', d.city);
      if (d.state) setValue('state', d.state);
    });
  }, [zip, setValue]);

  const email = watch('email');
  const [emailTakenOpen, setEmailTakenOpen] = useState(false);
  const shownForEmail = useRef('');
  useEffect(() => {
    const value = (email || '').trim().toLowerCase();
    // Trocou o e-mail depois de um erro (ex.: "já tem conta") — o aviso antigo
    // não vale mais pra esse valor novo, então some da tela.
    if (error) clearError();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    let active = true;
    const t = setTimeout(async () => {
      const exists = await checkEmailExists(value);
      if (active && exists && shownForEmail.current !== value) {
        shownForEmail.current = value;
        setEmailTakenOpen(true);
      }
    }, 500);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [email]);

  // Dispara os eventos de "entrada na etapa" a cada troca — a 1 (Seu carro)
  // concentra o que antes eram as etapas de perfil e plano; a 2 (Pagamento)
  // corresponde à antiga etapa de dados do usuário.
  useEffect(() => {
    if (step === 1) {
      pushGTMEvent({ event: 'start_perfil' });
      pushGTMEvent({ event: 'start_plano' });
    } else {
      pushGTMEvent({ event: 'form_start' });
    }
  }, [step]);

  const goToStep2 = () => {
    if (!ready) return;
    pushGTMEvent({ event: 'checkout_step_advance', step_index: 1, step_name: CHECKOUT_STEP_NAMES[1] });
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Botão só ativa com o formulário inteiro válido — dados, endereço,
  // cartão e os dois aceites obrigatórios (privacidade + termos).
  const canSubmit = isValid;

  const onSubmit = async (data: FormData) => {
    if (!selectedPlan || !canSubmit) return;
    try {
      await submitCheckout({
        plan: selectedPlan,
        isProfessional: !!isProfessional,
        installments: selectedPlan.interval_type === 'YEAR' ? installments : undefined,
        user: {
          full_name: data.full_name,
          email: data.email,
          document: data.document,
          phone: data.phone,
          password: data.password,
          receive_communications: !!data.receive_communications,
          privacy_terms: !!data.privacy_terms,
          program_rules: !!data.program_rules,
        },
        address: {
          street: data.street,
          number: data.number,
          complement: data.complement || undefined,
          neighborhood: data.neighborhood,
          city: data.city,
          state: data.state,
          zip_code: data.zip_code,
        },
        payment: {
          cardNumber: data.cardNumber,
          cardName: data.cardName,
          expiration: data.expiration,
          cvv: data.cvv,
          couponCode: data.couponCode || undefined,
          acceptedTerms: true,
        },
      });
      setDone(true);
    } catch {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const maxInstallments =
    selectedPlan && (selectedPlan.max_installments ?? 0) >= 2 ? selectedPlan.max_installments! : 12;
  const installmentBase = selectedPlan ? selectedPlan.installment_price ?? selectedPlan.price : 0;

  const totalPriceLabel = selectedPlan ? `R$ ${formatPrice(monthlyPrice(selectedPlan))}` : 'R$ --';
  const barLabel = step === 2 ? 'Total hoje' : ready ? 'Seu plano' : 'Selecione';
  const barReady = step === 2 ? canSubmit : ready;

  const checkbox = (checked: boolean, onChange: (v: boolean) => void, label: React.ReactNode) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, cursor: 'pointer' }} onClick={() => onChange(!checked)}>
      <Box
        sx={{
          flexShrink: 0,
          mt: '1px',
          width: 20,
          height: 20,
          borderRadius: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: checked ? CYAN : 'white',
          border: '1.5px solid',
          borderColor: checked ? CYAN : '#C9CED6',
          transition: 'all .2s',
        }}
      >
        {checked && <IconCheckSm />}
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.4, color: FEATURE_TEXT }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: '#EDEEF1', minHeight: '100vh' }}>
      <AppHeader variant="checkout" onBack={step === 2 ? () => setStep(1) : undefined} />

      {done && selectedPlan && (
        <SuccessOverlay
          line={`${selectedPlan.name} · R$ ${formatPrice(monthlyPrice(selectedPlan))}/mês`}
          onOpenApp={() => navigate('/app-download')}
        />
      )}

      <Dialog open={emailTakenOpen} onClose={() => setEmailTakenOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>E-mail já cadastrado</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: TEXT_MUTED }}>
            Este e-mail já tem uma conta WashPass. Faça login no app para fazer a assinatura.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEmailTakenOpen(false)}
            variant="contained"
            sx={{ borderRadius: '10px', backgroundColor: CYAN, fontWeight: 700, '&:hover': { backgroundColor: '#0083b3' } }}
          >
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      {/* faixa azul-marinho — muda de texto por etapa */}
      <Box sx={{ backgroundColor: NAVY, pt: '88px', px: { xs: 2.75, sm: 3.5 }, pb: step === 1 ? '82px' : '26px' }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.656rem', letterSpacing: '1.6px', color: 'rgba(255,255,255,.9)', mb: 0.75 }}>
            ASSINATURA ONLINE
          </Typography>
          <Typography
            component="h1"
            sx={{ fontWeight: 800, fontSize: step === 1 ? '1.875rem' : '1.625rem', lineHeight: 1.15, letterSpacing: '-0.7px', color: 'white', mb: 1 }}
          >
            {step === 1 ? 'Assine o WashPass' : 'Pagamento'}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.5, color: 'rgba(255,255,255,.88)', maxWidth: 320 }}>
            {step === 1
              ? 'Escolha seu carro, pague pelo site e pronto — o app já vem com tudo configurado.'
              : 'Pagamento no cartão, com criptografia SSL.'}
          </Typography>
        </Box>
      </Box>

      {/* cartão de conteúdo — sobrepõe a faixa azul */}
      <Box
        sx={{
          maxWidth: 480,
          mx: 'auto',
          mt: step === 1 ? '-58px' : 0,
          backgroundColor: step === 1 ? 'white' : STEP2_BG,
          borderRadius: step === 1 ? '26px 26px 0 0' : 0,
          boxShadow: step === 1 ? '0 -12px 30px rgba(31,41,51,.12)' : 'none',
          position: 'relative',
          px: { xs: 2.75, sm: 3.5 },
          pt: 2.75,
          pb: '150px',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}
        {plansError && !error && step === 1 && (
          <Alert severity="warning" sx={{ mb: 2.5, borderRadius: '12px' }}>
            {plansError}
          </Alert>
        )}

        {step === 1 && <Stepper step={step} />}

        {step === 1 ? (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.19rem', color: TEXT, letterSpacing: '-0.3px', mb: 0.5 }}>
              Tamanho do carro
            </Typography>
            <Typography sx={{ fontSize: '0.844rem', lineHeight: 1.45, color: TEXT_HINT, mb: 1.75 }}>
              Usamos o tamanho pra calcular o valor da lavagem.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3.5 }}>
              <OptionCard
                icon={<IconHatch />}
                label="Hatch / Sedan"
                hint="carro compacto ou médio"
                selected={carType === 'hatch'}
                onClick={() => pickCarType('hatch')}
              />
              <OptionCard
                icon={<IconSuv />}
                label="SUV / grande"
                hint="SUV, caminhonete"
                selected={carType === 'suv'}
                onClick={() => pickCarType('suv')}
              />
            </Box>

            <Typography sx={{ fontWeight: 800, fontSize: '1.19rem', color: TEXT, letterSpacing: '-0.3px', mb: 0.5 }}>
              Uso do veículo
            </Typography>
            <Typography sx={{ fontSize: '0.844rem', lineHeight: 1.45, color: TEXT_HINT, mb: 1.75 }}>
              Uso profissional lava com mais frequência, então tem valor próprio.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3.5 }}>
              <OptionCard
                icon={<IconPerson />}
                label="Pessoal"
                hint="meu carro do dia a dia"
                selected={isProfessional === false}
                onClick={() => pickProfessional(false)}
              />
              <OptionCard
                icon={<IconPro />}
                label="Profissional"
                hint="Uber, 99, táxi"
                selected={isProfessional === true}
                onClick={() => pickProfessional(true)}
              />
            </Box>

            <Typography sx={{ fontWeight: 800, fontSize: '1.19rem', color: TEXT, letterSpacing: '-0.3px', mb: 0.5 }}>
              Escolha seu plano
            </Typography>
            <Typography sx={{ fontSize: '0.844rem', lineHeight: 1.45, color: TEXT_HINT, mb: 1.5 }}>
              Compare os benefícios e escolha.
            </Typography>

            <Box sx={{ p: 1.5, borderRadius: '22px', backgroundColor: PLAN_BOX_BG, border: `1.5px solid #E1E5EB` }}>
              {hasYearly && (
                <Box sx={{ mb: 1.75 }}>
                  <Typography sx={{ display: 'block', mb: 1.1, fontWeight: 800, fontSize: '0.8125rem', color: NAVY }}>
                    Ciclo de cobrança
                  </Typography>
                  <Box sx={{ position: 'relative', display: 'flex', p: '4px', backgroundColor: 'white', borderRadius: '999px', border: '1.5px solid #D6DBE2' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: period === 'MONTH' ? 4 : '50%',
                        width: 'calc(50% - 8px)',
                        height: 'calc(100% - 8px)',
                        borderRadius: '999px',
                        backgroundColor: NAVY,
                        boxShadow: '0 4px 12px -4px rgba(47,56,87,.6)',
                        transition: 'left .35s cubic-bezier(.65,.02,.24,1)',
                      }}
                    />
                    {(['MONTH', 'YEAR'] as const).map((pv) => (
                      <Box
                        key={pv}
                        onClick={() => pickPeriod(pv)}
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          flex: 1,
                          textAlign: 'center',
                          py: 1.25,
                          borderRadius: '999px',
                          cursor: 'pointer',
                        }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', color: period === pv ? 'white' : NAVY }}>
                          {pv === 'MONTH' ? 'Mensal' : 'Anual'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Typography sx={{ m: '0 4px 10px', fontWeight: 800, fontSize: '0.8125rem', color: NAVY }}>
                Planos disponíveis
              </Typography>

              {!size || isProfessional === null ? (
                <Typography sx={{ color: TEXT_HINT, fontSize: '0.85rem', px: 0.5, pb: 0.5 }}>
                  {emptyHint}
                </Typography>
              ) : plansLoading && !scopedPlans.length ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={26} sx={{ color: CYAN }} />
                </Box>
              ) : !scopedPlans.length ? (
                <Alert severity="info" sx={{ borderRadius: '12px' }}>Nenhum plano disponível para esta seleção.</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {scopedPlans.map((p) => (
                    <PlanCard
                      key={p.id}
                      plan={p}
                      selected={planId === p.id}
                      onClick={() => {
                        setPlanId(p.id);
                        setInstallments(1);
                        pushGTMEvent({ event: 'complete_plano' });
                        pushGTMEvent({
                          event: 'checkout_plan_selected',
                          plan_id: p.id,
                          plan_name: p.name,
                          plan_price: p.price,
                          plan_interval: p.interval_type,
                        });
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <PlanSummaryBox
              planName={selectedPlan ? selectedPlan.name : 'Nenhum plano selecionado'}
              sizeLabel={sizeLabel}
              useLabel={useLabel}
              cycleLabel={cycleLabel}
              totalPrice={totalPriceLabel}
              yearNote={selectedPlan?.interval_type === 'YEAR' ? `cobrado anualmente — R$ ${formatPrice(selectedPlan.price)}/ano` : undefined}
              emptyHint={!ready ? emptyHint : undefined}
              features={
                selectedPlan
                  ? [
                      ...(selectedPlan.carwash_count ? [`${selectedPlan.carwash_count} locais na rede`] : []),
                      ...(selectedPlan.plan_services ?? []).map(
                        (s) => `${s.service.name}${s.limit > 0 ? ` (até ${s.limit}x/mês)` : ''}`,
                      ),
                    ]
                  : []
              }
            />

            <Typography sx={{ mt: 2.25, fontSize: '0.72rem', lineHeight: 1.5, color: TEXT_FAINT, textAlign: 'center' }}>
              Wash Serviços De Tecnologia Ltda. · CNPJ 52.472.237/0001-79
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.25, mt: 1 }}>
              <Link href="/client-privacy-policy" target="_blank" sx={{ fontSize: '0.72rem', fontWeight: 600, color: NAVY }}>
                Política de privacidade
              </Link>
              <Link href="/fale-conosco" target="_blank" sx={{ fontSize: '0.72rem', fontWeight: 600, color: NAVY }}>
                Suporte
              </Link>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit(onSubmit, () => setSubmitAttempted(true))}>
            <Box sx={{ backgroundColor: 'white', borderRadius: '18px', border: `1px solid ${BORDER}`, p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: TEXT }}>
                  {selectedPlan?.name ?? 'Plano'}
                </Typography>
                <Link component="button" type="button" onClick={() => setStep(1)} sx={{ fontWeight: 700, fontSize: '0.78rem', color: NAVY }}>
                  Alterar
                </Link>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '0.8125rem', color: TEXT_HINT }}>Carro</Typography>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: TEXT }}>{sizeLabel}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1.5, borderBottom: '1px solid #EDEEF1' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: TEXT_HINT }}>Uso</Typography>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: TEXT }}>{useLabel}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.844rem', color: TEXT }}>Total hoje</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.375rem', color: TEXT, letterSpacing: '-0.8px' }}>
                  {totalPriceLabel}
                </Typography>
              </Box>
            </Box>

            <SectionHeading>Seus dados</SectionHeading>
            <FormBox>
              <Controller
                name="full_name"
                control={control}
                render={({ field }) => (
                  <Field label="Nome completo" error={shownError('full_name')}>
                    <OutlinedInput {...field} fullWidth placeholder="Seu nome completo" sx={fieldInputSx(!!shownError('full_name'))} />
                  </Field>
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Field label="E-mail" error={shownError('email')}>
                    <OutlinedInput {...field} fullWidth type="email" placeholder="seu@email.com" sx={fieldInputSx(!!shownError('email'))} />
                  </Field>
                )}
              />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Field label="Celular" error={shownError('phone')}>
                        <OutlinedInput {...field} fullWidth inputComponent={PhoneMask as never} sx={fieldInputSx(!!shownError('phone'))} />
                      </Field>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="document"
                    control={control}
                    render={({ field }) => (
                      <Field label="CPF" error={shownError('document')}>
                        <OutlinedInput {...field} fullWidth inputComponent={CpfMask as never} sx={fieldInputSx(!!shownError('document'))} />
                      </Field>
                    )}
                  />
                </Grid>
              </Grid>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Field label="Crie uma senha" error={shownError('password')}>
                    <OutlinedInput {...field} fullWidth type="password" placeholder="Mínimo 6 caracteres" sx={fieldInputSx(!!shownError('password'))} />
                  </Field>
                )}
              />
              <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: TEXT_HINT }}>
                Você usa essa senha para entrar no app depois.
              </Typography>
            </FormBox>

            <SectionHeading>Endereço</SectionHeading>
            <FormBox>
              <Grid container spacing={1.5}>
                <Grid item xs={7}>
                  <Controller
                    name="zip_code"
                    control={control}
                    render={({ field }) => (
                      <Field label="CEP" error={shownError('zip_code')}>
                        <OutlinedInput {...field} fullWidth inputComponent={CepMask as never} sx={fieldInputSx(!!shownError('zip_code'))} />
                      </Field>
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Controller
                    name="number"
                    control={control}
                    render={({ field }) => (
                      <Field label="Número" error={shownError('number')}>
                        <OutlinedInput {...field} fullWidth placeholder="123" sx={fieldInputSx(!!shownError('number'))} />
                      </Field>
                    )}
                  />
                </Grid>
              </Grid>
              <Controller
                name="street"
                control={control}
                render={({ field }) => (
                  <Field label="Rua" error={shownError('street')}>
                    <OutlinedInput {...field} fullWidth placeholder="Nome da rua" sx={fieldInputSx(!!shownError('street'))} />
                  </Field>
                )}
              />
              <Controller
                name="neighborhood"
                control={control}
                render={({ field }) => (
                  <Field label="Bairro" error={shownError('neighborhood')}>
                    <OutlinedInput {...field} fullWidth placeholder="Seu bairro" sx={fieldInputSx(!!shownError('neighborhood'))} />
                  </Field>
                )}
              />
              <Grid container spacing={1.5}>
                <Grid item xs={8}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Field label="Cidade" error={shownError('city')}>
                        <OutlinedInput {...field} fullWidth placeholder="Sua cidade" sx={fieldInputSx(!!shownError('city'))} />
                      </Field>
                    )}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <Field label="UF" error={shownError('state')}>
                        <OutlinedInput
                          {...field}
                          fullWidth
                          placeholder="SP"
                          inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                          sx={fieldInputSx(!!shownError('state'))}
                        />
                      </Field>
                    )}
                  />
                </Grid>
              </Grid>
              <Controller
                name="complement"
                control={control}
                render={({ field }) => (
                  <Field label="Complemento (opcional)">
                    <OutlinedInput {...field} fullWidth placeholder="Apto, bloco, referência" sx={fieldInputSx()} />
                  </Field>
                )}
              />
            </FormBox>

            <SectionHeading>Pagamento</SectionHeading>
            <FormBox>
              <Controller
                name="cardNumber"
                control={control}
                render={({ field }) => (
                  <Field label="Número do cartão" error={shownError('cardNumber')}>
                    <OutlinedInput {...field} fullWidth inputComponent={CardNumberMask as never} placeholder="0000 0000 0000 0000" sx={fieldInputSx(!!shownError('cardNumber'))} />
                  </Field>
                )}
              />
              <Controller
                name="cardName"
                control={control}
                render={({ field }) => (
                  <Field label="Nome no cartão" error={shownError('cardName')}>
                    <OutlinedInput {...field} fullWidth placeholder="Como está impresso" sx={fieldInputSx(!!shownError('cardName'))} />
                  </Field>
                )}
              />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Controller
                    name="expiration"
                    control={control}
                    render={({ field }) => (
                      <Field label="Validade" error={shownError('expiration')}>
                        <OutlinedInput {...field} fullWidth inputComponent={CardExpirationMask as never} placeholder="MM/AA" sx={fieldInputSx(!!shownError('expiration'))} />
                      </Field>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="cvv"
                    control={control}
                    render={({ field }) => (
                      <Field label="CVV" error={shownError('cvv')}>
                        <OutlinedInput {...field} fullWidth inputComponent={CvvMask as never} placeholder="123" sx={fieldInputSx(!!shownError('cvv'))} />
                      </Field>
                    )}
                  />
                </Grid>
              </Grid>
              {selectedPlan?.interval_type === 'YEAR' && maxInstallments > 1 && (
                <Field label="Parcelamento">
                  <Select
                    fullWidth
                    value={installments}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      setInstallments(n);
                      pushGTMEvent({
                        event: 'checkout_payment_format_selected',
                        format: n === 1 ? 'avista' : 'parcelado',
                        installments: n,
                      });
                    }}
                    sx={fieldInputSx()}
                  >
                    <MenuItem value={1}>À vista — R$ {formatPrice(selectedPlan.price)}</MenuItem>
                    {Array.from({ length: maxInstallments - 1 }, (_, i) => i + 2).map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}x de R$ {formatPrice(installmentBase / n)}
                      </MenuItem>
                    ))}
                  </Select>
                </Field>
              )}
              <Controller
                name="couponCode"
                control={control}
                render={({ field }) => (
                  <Field label="Cupom (opcional)">
                    <OutlinedInput {...field} fullWidth placeholder="Seu código" sx={fieldInputSx()} />
                  </Field>
                )}
              />
            </FormBox>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 0.25, mb: 2.5 }}>
              <Controller
                name="privacy_terms"
                control={control}
                render={({ field }) =>
                  checkbox(!!field.value, field.onChange, (
                    <>
                      Li e concordo com a{' '}
                      <Link href="/client-privacy-policy" target="_blank" sx={{ color: CYAN, fontWeight: 700 }}>
                        política de privacidade
                      </Link>
                    </>
                  ))
                }
              />
              {shownError('privacy_terms') && (
                <FormHelperText sx={{ color: ERROR, fontWeight: 600, ml: 4, mt: -1 }}>{shownError('privacy_terms')}</FormHelperText>
              )}
              <Controller
                name="program_rules"
                control={control}
                render={({ field }) =>
                  checkbox(!!field.value, field.onChange, (
                    <>
                      Li e concordo com os{' '}
                      <Link href="/termos-e-condicoes" target="_blank" sx={{ color: CYAN, fontWeight: 700 }}>
                        termos e condições
                      </Link>
                    </>
                  ))
                }
              />
              {shownError('program_rules') && (
                <FormHelperText sx={{ color: ERROR, fontWeight: 600, ml: 4, mt: -1 }}>{shownError('program_rules')}</FormHelperText>
              )}
              <Controller
                name="receive_communications"
                control={control}
                render={({ field }) => checkbox(!!field.value, field.onChange, 'Quero receber novidades e ofertas')}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.1, px: 0.5 }}>
              <IconLock />
              <Typography sx={{ fontSize: '0.75rem', lineHeight: 1.45, color: TEXT_HINT }}>
                Pagamento com criptografia SSL · o veículo você cadastra depois no app
              </Typography>
            </Box>

            {/* submit real (a barra flutuante é só visual/atalho) */}
            <LoadingButton type="submit" loading={loading} sx={{ display: 'none' }} id="checkout-submit" />
          </Box>
        )}
      </Box>

      {!done && (
        <FloatingBar
          label={barLabel}
          price={totalPriceLabel}
          ready={barReady}
          ctaLabel={step === 1 ? 'Ir Para Pagamento' : 'Assinar Agora'}
          ctaIcon={step === 1 ? <IconArrowForward /> : <IconLockWhite />}
          onClick={() => {
            if (step === 1) {
              goToStep2();
            } else if (canSubmit) {
              document.getElementById('checkout-submit')?.click();
            }
          }}
        />
      )}
    </Box>
  );
};

const IconLockWhite = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm3 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
  </svg>
);

export default CheckoutForm;
